import { Component, Input } from '@angular/core';

import { translatedNoteValue } from '@services/noteTranslation/note-translation';
import { UserdataService } from '@services/userdata/userdata.service';

@Component({
  selector: 'app-note-list-item',
  templateUrl: './note-list-item.component.html',
  styleUrls: ['./note-list-item.component.scss'],
})
export class NoteListItemComponent {
  // A setter rather than ngOnChanges: this resolves correctly whether the note
  // arrives through a template binding or is assigned straight onto the
  // instance (which is how the spec drives it, and ngOnChanges never fires for
  // that path).
  @Input()
  set note(value: any) {
    this.noteData = value;
    this.resolveTranslation();
  }

  get note(): any {
    return this.noteData;
  }

  /**
   * QA round 1 (!2201): the seven observation detail pages this component was
   * extracted from all rendered an avatar, so it is the default. The two
   * CLOSING-note hosts did not — `closing-details` and `closing-notes` sit
   * outside `.detail-page-section`, which is where the only rules that size
   * `.user-img` live (`_observations-pages.scss:132,195`), so an avatar there
   * renders unsized and unrounded. Suppressing it is also what those two
   * screens looked like before the extraction.
   *
   * Opt-OUT rather than opt-in on purpose: the default preserves the behaviour
   * of the seven pages that make up the bulk of the call sites, so a new caller
   * that forgets the input gets the common case rather than a silent regression.
   */
  @Input() public showAvatar = true;

  // Pre-computed for the template (no function calls in templates).
  public text = '';
  public canShowOriginal = false;
  public showingOriginal = false;

  private noteData: any;
  private original: string;
  private translated: string = null;

  constructor(private userdataService: UserdataService) {}

  public toggleOriginal(): void {
    this.showingOriginal = !this.showingOriginal;
    this.text = this.showingOriginal ? this.noteData.data : this.translated;
  }

  private resolveTranslation(): void {
    // The toggle appears only when a translation was actually substituted, so
    // it is driven by the resolver disagreeing with the original rather than by
    // the mere presence of a `translations` field. Everything needed is already
    // in the payload, so flipping it costs no network.
    //
    // Audio notes are excluded outright: they come from
    // `observation.attachments`, which the translation worker never touches,
    // and their `data` is an object URI rather than text.
    //
    // Reads getLanguage() rather than the active ngx-translate language on
    // purpose: AppComponent.initializeApp() hardcodes translate.use('en'), so
    // the neighbouring getDefaultLang() idiom would resolve to English for
    // every viewer. That quirk is pre-existing and is not fixed here.
    // Only drop the reader's choice when this is genuinely a DIFFERENT note.
    // These lists are rebuilt wholesale on every ccs:noteUpdate /
    // ccs:observationUpdate, so resetting on any rebind snapped the reader back
    // to the translation mid-read whenever anyone else touched the observation.
    const previousOriginal = this.original;
    const original = this.noteData?.data;
    this.original = original;

    const resolved = this.noteData?.type === 'text'
      ? translatedNoteValue(
          // The note feeders rename the note's `value` to `data`.
          { value: original, language: this.noteData.language, translations: this.noteData.translations },
          this.userdataService.getLanguage())
      : original;

    this.translated = resolved !== original ? resolved : null;
    this.canShowOriginal = this.translated !== null;
    if (original !== previousOriginal || !this.canShowOriginal) {
      this.showingOriginal = false;
    }
    this.text = this.showingOriginal ? original : (this.translated ?? original);
  }
}
